/**
 * Workspace configuration (WP-12, CONTINUOUS_SESSIONS_SPEC_v1 §4.8 T2) —
 * typed wrappers over:
 *   GET  /api/cards/:cardId/workspace-config
 *   POST /api/cards/:cardId/workspace-config/accept  {path, sha256}
 *
 * Mirrors apps/chaos-gateway/src/workspace-config-routes.ts.
 */
import { apiJson } from './client';

export type WorkspaceConfigKind = 'cli-config' | 'git-config' | 'git-hooks';

export type WorkspaceConfigShapeReason =
  | 'link_escapes_workspace'
  | 'include_escapes_workspace'
  | 'gitdir_outside_workspace'
  | 'hook_is_link'
  | 'not_a_regular_file'
  | 'include_depth_exceeded'
  | 'config_too_large'
  | 'too_many_referenced_files'
  | 'workspace_scan_budget_exceeded'
  | 'workspace_unverifiable'
  | 'executable_config_unsupported'
  | 'config_not_owned';

export interface WorkspaceConfigWriter {
  userId: string | null;
  name: string | null;
  runId: string;
  sessionId: string;
  stepStartedAt: string;
}

/**
 * A risky construct the gateway found in a CLI config file, by the gateway's
 * own MCP config rules (apps/chaos-gateway/src/lib/workspace-config-risks.ts).
 * The UI never parses the file: it shows these codes as sentences.
 */
export type WorkspaceConfigRiskCode =
  | 'unparsed'
  | 'not_checked'
  | 'headers_helper'
  | 'env_reference'
  | 'remote_bearer'
  | 'shell_command'
  | 'command_key'
  | 'stdio_server'
  | 'token_env'
  | 'remote_url';

export interface WorkspaceConfigRisk {
  code: WorkspaceConfigRiskCode;
  /** Where in the file (`mcpServers.x.headers.Authorization`); never a value. */
  path: string;
}

export interface WorkspaceConfigItem {
  policyVersion?: string;
  /** Owner/admin only; structural location, never configuration values. */
  policyIssue?: { code: 'command' | 'environment' | 'autoload' | 'schema' | 'unparsed' | 'budget'; location: string };
  path: string;
  kind: WorkspaceConfigKind;
  sha256: string | null;
  reason?: WorkspaceConfigShapeReason;
  keys?: string[];
  hooks?: string[];
  /** CLI config files the gateway reads structurally; absent for the rest (`.env`). */
  risks?: WorkspaceConfigRisk[];
  /** Risks beyond the gateway's per-file cap, counted, not listed. */
  risksOmitted?: number;
  /** The gateway stopped collecting early: `risksOmitted` is a lower bound. */
  risksTruncated?: boolean;
  /**
   * `settings`: a file whose meaning the gateway's check models (MCP and
   * settings files), so "nothing flagged" means nothing it knows to be risky.
   * `other`: any other `.gemini/` / `.codex/` file; only generic rules ran.
   */
  risksScope?: 'settings' | 'other';
  /** Workspace files this entry names as programs to run; their digests are part of what Accept accepts. */
  refs?: Array<{ path: string; sha256: string }>;
  accepted: boolean;
  acceptedBy: { userId: string | null; name: string | null; at: string } | null;
  lastChangedAt: string | null;
  lastChangedBy: WorkspaceConfigWriter | null;
  /** Only for someone who may accept. */
  preview?: string;
  /**
   * R-a owned mode only: `placeholder` = CHAOS's inert copy (needs no review),
   * `installed` = bytes someone accepted, `not_owned` = not CHAOS's copy.
   */
  ownedState?: 'placeholder' | 'installed' | 'not_owned';
  /** R-a owned mode: this person may accept the installed file for their OWN steps. */
  canAcceptForSelf?: boolean;
}

/** R-a owned mode, as the card's workspace stands. */
export type WorkspaceConfigOwnedMode = 'off' | 'unsupported' | 'not_enrolled' | 'enrolled';

/** A change an agent proposed for a guarded file (`.chaos/proposed/<path>`). */
export interface WorkspaceConfigProposal {
  path: string;
  sha256: string;
  byteLength: number;
  /** `own`: the only person whose agents use this workspace; `unknown`: it cannot be told. */
  attribution: 'own' | 'unknown';
  proposerUserId: string | null;
  proposerName: string | null;
  attributionWhy?: 'shared_workspace' | 'no_executor' | 'unattributed_run';
  firstSeenAt: string | null;
  notAcceptable: boolean;
  /** Small enough to be read whole here; otherwise only Reject is offered. */
  reviewable?: boolean;
  replaces: 'placeholder' | 'installed' | 'none';
  canDecide: boolean;
  preview?: string;
  risks?: WorkspaceConfigRisk[];
  riskCount: number;
}

/** An installed file this person's agent would run that they have not reviewed. */
export interface WorkspaceConfigNeedsReview {
  path: string;
  rawSha256: string;
  preview?: string;
}

export interface WorkspaceConfigProposalsListing {
  owned: { mode: WorkspaceConfigOwnedMode };
  proposals: WorkspaceConfigProposal[];
  needsMyReview: WorkspaceConfigNeedsReview[];
  unverifiable?: boolean;
}

export interface WorkspaceConfigListing {
  guardEnabled: boolean;
  canAccept: boolean;
  untrustedCount: number;
  /** Absent on a gateway without R-a. */
  owned?: { mode: WorkspaceConfigOwnedMode };
  proposals?: WorkspaceConfigProposal[];
  items: WorkspaceConfigItem[];
}

export const workspaceConfigKeys = {
  card: (cardId: string) => ['workspace-config', cardId] as const,
  proposals: (cardId: string) => ['workspace-config', cardId, 'proposals'] as const,
};

export const workspaceConfigApi = {
  get(cardId: string): Promise<WorkspaceConfigListing> {
    return apiJson<WorkspaceConfigListing>(`/cards/${cardId}/workspace-config`);
  },
  accept(cardId: string, input: { path: string; sha256: string }): Promise<{ accepted: { path: string; sha256: string } }> {
    return apiJson(`/cards/${cardId}/workspace-config/accept`, { method: 'POST', json: input });
  },
  proposals(cardId: string): Promise<WorkspaceConfigProposalsListing> {
    return apiJson<WorkspaceConfigProposalsListing>(`/cards/${cardId}/workspace-config/proposals`);
  },
  decideProposal(
    cardId: string,
    decision: 'accept' | 'reject',
    input: { path: string; sha256: string },
  ): Promise<Record<string, { path: string; sha256: string }>> {
    return apiJson(`/cards/${cardId}/workspace-config/proposals/${decision}`, { method: 'POST', json: input });
  },
  acceptInstalled(cardId: string, input: { path: string; rawSha256: string }): Promise<{ accepted: { path: string } }> {
    return apiJson(`/cards/${cardId}/workspace-config/accept-installed`, { method: 'POST', json: input });
  },
  reset(cardId: string, input: { path: string }): Promise<{ reset: { path: string } }> {
    return apiJson(`/cards/${cardId}/workspace-config/reset`, { method: 'POST', json: input });
  },
};

/** Why a proposal's author cannot be told, in one sentence. */
export function attributionText(p: WorkspaceConfigProposal): string {
  if (p.attribution === 'own') return `Proposed by ${p.proposerName ?? 'your'}${p.proposerName ? "'s" : ''} own agent.`;
  if (p.attributionWhy === 'shared_workspace') {
    return "More than one person's agents use this workspace, so who proposed this cannot be told. Review it as someone else's.";
  }
  return 'Who proposed this cannot be told. Review it as someone else\'s.';
}

/** One sentence per shape refusal — never shows a link target. */
export function shapeReasonText(reason: WorkspaceConfigShapeReason): string {
  switch (reason) {
    case 'link_escapes_workspace':
      return 'A link that points outside the card workspace. Remove it.';
    case 'include_escapes_workspace':
      return 'Includes a git config file outside the card workspace. Remove the include.';
    case 'gitdir_outside_workspace':
      return 'Points git at a directory outside the card workspace. Remove it.';
    case 'hook_is_link':
      return 'A git hook that is a link. Replace it with a real file or remove it.';
    case 'not_a_regular_file':
      return 'Not a regular file. Remove it.';
    case 'include_depth_exceeded':
      return 'Git config includes nested more than three levels deep. Flatten them.';
    case 'config_too_large':
      return 'Too large to review (over 1 MiB for a config file, 16 MiB for a hook or a script it runs).';
    case 'too_many_referenced_files':
      return 'Names more than 64 workspace files to run, or runs them from more than 16 folders. Point it at fewer scripts.';
    case 'workspace_scan_budget_exceeded':
      return 'The workspace holds too many files, folders too deep, or too much configuration to check (over 100,000 entries, paths over 3,072 bytes, over 64 MiB of configuration and the scripts it names, or commands that take over 4,000,000 characters or 100,000 path lookups to check).';
    case 'executable_config_unsupported':
      return 'This automatic configuration is outside the supported data forms and cannot be accepted. Remove them; run project tools explicitly in a step instead. Hashing a script does not freeze what it loads.';
    case 'workspace_unverifiable':
      return 'The workspace could not be opened safely.';
    case 'config_not_owned':
      return 'This is not the copy CHAOS owns, so an agent could still change it. It is put back automatically before the next step.';
    default:
      return 'This entry cannot be accepted as it is.';
  }
}

export function kindLabel(kind: WorkspaceConfigKind): string {
  if (kind === 'git-config') return 'Git settings that run programs';
  if (kind === 'git-hooks') return 'Git hooks';
  return 'AI tool configuration';
}

/** The constructs that hand a session's tokens to someone else: shown in red. */
export function riskIsSevere(code: WorkspaceConfigRiskCode): boolean {
  return (
    code === 'unparsed' ||
    code === 'not_checked' ||
    code === 'headers_helper' ||
    code === 'env_reference' ||
    code === 'remote_bearer' ||
    code === 'shell_command'
  );
}

/** One sentence per risk code. */
export function riskText(code: WorkspaceConfigRiskCode): string {
  switch (code) {
    case 'unparsed':
      return 'Could not be read as JSON or TOML, so nothing in it was checked. Review the raw content line by line.';
    case 'not_checked':
      return 'Not checked: this listing already spent its checking budget on other files. Review the raw content line by line.';
    case 'headers_helper':
      return 'headersHelper: a command the AI tool runs to make request headers, with this session\'s tokens in its environment.';
    case 'env_reference':
      return 'A ${...} reference. The AI tool fills it in from its own environment, which holds this session\'s tokens.';
    case 'remote_bearer':
      return 'Sends a credential to a remote server: an auth header, a token setting, a token in the address, or a changed API address.';
    case 'shell_command':
      return 'Starts a shell or runs inline code.';
    case 'command_key':
      return 'Runs a command.';
    case 'stdio_server':
      return 'Starts a local MCP server program in the agent container, with its environment.';
    case 'token_env':
      return 'Sets or passes on an environment variable that looks like a secret.';
    case 'remote_url':
      return 'Connects to a remote server.';
    default:
      return 'A setting to review.';
  }
}
